import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'motion/react'
import { Menu, X, ChevronDown, Phone, ExternalLink, Calendar } from 'lucide-react'
import { Button } from '@/components/ui'
import { mainNav } from '@/data/navigation'
import { companyInfo } from '@/data/company'
import { cn } from '@/lib/utils'

const CALENDAR_LINK = 'https://meetings.hubspot.com/markus98/ucadvisors-quick-chat?embed=true&uuid=052b5298-7c6e-4b12-bde4-f8f26d01c9b3'

export function Header() {
  const [isOpen, setIsOpen] = useState(false)
  const [openDropdown, setOpenDropdown] = useState<string | null>(null)
  const location = useLocation()

  const isActive = (href: string) => {
    if (href === '/') return location.pathname === '/'
    return location.pathname.startsWith(href)
  }

  return (
    <header className="fixed top-0 left-0 right-0 z-50">
      {/* Top bar */}
      <div className="bg-primary-600/80 backdrop-blur-sm border-b border-white/5">
        <div className="container-custom py-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-slate-400 hidden sm:block">
              Monday - Friday | 6 AM - 6 PM PST
            </span>
            <div className="flex items-center gap-6">
              <a
                href={`tel:${companyInfo.phoneRaw}`}
                className="flex items-center gap-2 text-accent-400 hover:text-accent-300 transition-colors"
              >
                <Phone className="w-4 h-4" />
                {companyInfo.phone}
              </a>
              <a
                href="https://onboarding.ucadvisors.com"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-emerald-400 hover:text-emerald-300 transition-colors font-medium"
              >
                <ExternalLink className="w-4 h-4" />
                Client Portal
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Main nav */}
      <div className="bg-primary-500/90 backdrop-blur-md border-b border-white/10">
        <div className="container-custom">
          <nav className="flex items-center justify-between h-20 md:h-24">
            {/* Logo */}
            <Link to="/" className="flex items-center">
              <img 
                src="/logo.png" 
                alt="UC Advisors" 
                className="h-11 md:h-14 w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-1">
              {mainNav.map((item) => (
                <div
                  key={item.href}
                  className="relative"
                  onMouseEnter={() => item.children && setOpenDropdown(item.href)}
                  onMouseLeave={() => setOpenDropdown(null)}
                >
                  <Link
                    to={item.href}
                    className={cn(
                      'flex items-center gap-1 px-4 py-2 text-sm font-medium rounded-lg transition-colors',
                      isActive(item.href)
                        ? 'text-accent-400'
                        : 'text-slate-300 hover:text-white hover:bg-white/5'
                    )}
                  >
                    {item.title}
                    {item.children && (
                      <ChevronDown
                        className={cn(
                          'w-4 h-4 transition-transform',
                          openDropdown === item.href && 'rotate-180'
                        )}
                      />
                    )}
                  </Link>

                  {/* Dropdown */}
                  <AnimatePresence>
                    {item.children && openDropdown === item.href && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        transition={{ duration: 0.2 }}
                        className="absolute top-full left-0 pt-2 w-80"
                      >
                        <div className="glass-card-solid p-2">
                          {item.children.map((child) => (
                            <Link
                              key={child.href}
                              to={child.href}
                              className="flex items-start gap-3 p-3 rounded-lg hover:bg-white/5 transition-colors group"
                            >
                              {child.icon && (
                                <div className="w-10 h-10 rounded-lg bg-accent-500/10 flex items-center justify-center flex-shrink-0 group-hover:bg-accent-500/20 transition-colors">
                                  <child.icon className="w-5 h-5 text-accent-400" />
                                </div>
                              )}
                              <div>
                                <div className="font-medium text-white group-hover:text-accent-400 transition-colors">
                                  {child.title}
                                </div>
                                {child.description && (
                                  <p className="text-sm text-slate-400 mt-0.5">
                                    {child.description}
                                  </p>
                                )}
                              </div>
                            </Link>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </div>

            {/* CTA */}
            <div className="hidden lg:flex items-center gap-4">
              <a href={CALENDAR_LINK} target="_blank" rel="noopener noreferrer">
                <Button variant="glow" size="lg" className="shadow-glow-lg px-6 group">
                  <Calendar className="w-4 h-4 mr-2" />
                  Book a 15-min Call
                </Button>
              </a>
            </div>

            {/* Mobile menu button */}
            <button
              className="lg:hidden p-2 text-white"
              onClick={() => setIsOpen(!isOpen)}
              aria-label="Toggle menu"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </nav>
        </div>
      </div>

      {/* Mobile Navigation */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-primary-600/95 backdrop-blur-md border-b border-white/10 overflow-hidden"
          >
            <div className="container-custom py-4">
              <div className="flex flex-col gap-2">
                {mainNav.map((item) => (
                  <div key={item.href}>
                    <Link
                      to={item.href}
                      className={cn(
                        'block px-4 py-3 text-base font-medium rounded-lg transition-colors',
                        isActive(item.href)
                          ? 'text-accent-400 bg-white/5'
                          : 'text-slate-300 hover:text-white hover:bg-white/5'
                      )}
                      onClick={() => !item.children && setIsOpen(false)}
                    >
                      {item.title}
                    </Link>
                    {item.children && (
                      <div className="ml-4 mt-1 space-y-1">
                        {item.children.map((child) => (
                          <Link
                            key={child.href}
                            to={child.href}
                            className="block px-4 py-2 text-sm text-slate-400 hover:text-white transition-colors"
                            onClick={() => setIsOpen(false)}
                          >
                            {child.title}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                <div className="pt-4 mt-2 border-t border-white/10">
                  <a href={CALENDAR_LINK} target="_blank" rel="noopener noreferrer" onClick={() => setIsOpen(false)}>
                    <Button variant="glow" className="w-full">
                      <Calendar className="w-4 h-4 mr-2" />
                      Book a 15-min Call
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  )
}

